/**
 * Raw HTTP for the auth endpoints.
 *
 * These functions intentionally bypass the shared api-client to avoid a
 * cycle (api-client depends on the auth store, which depends on these calls
 * to populate itself). They're the only place outside api-client.ts that
 * may use fetch() directly.
 *
 * The `IpAddress` header is load-bearing on every authenticated request:
 * see `src/shared/api/ip-address.ts` for the why (.NET gRPC pipeline
 * rejects null metadata after the DB write commits). api-client handles
 * this for the rest of the app; here we inject the header on every
 * authenticated wrapper too.
 */

import { DEV_CONFIG } from '@/shared/api/dev-config';
import { getClientIpAddress } from '@/shared/api/ip-address';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export interface LoginTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * One entry under `user.organizationLocations`. The location-filter buttons
 * in the calendar toolbar render one icon per entry (Flutter parity).
 *
 *   name → drives the icon (Office=Plus, Home=Home, Virtual=Video, else=Ban)
 *   id   → toggled in/out of the appointments-request `location[]` body
 */
export interface OrgLocation {
  id: string;
  name: string;
  imageUrl?: string;
}

/**
 * Per-permission CRUD flags. Backend returns one of these on every
 * `SystemPermission` entry; a check like "can the user edit demographics?"
 * is `permissionResults.edit === true`.
 */
export interface PermissionResults {
  add?: boolean;
  delete?: boolean;
  edit?: boolean;
  view?: boolean;
}

/**
 * One entry in `user.systemPermissions`. Mirrors Flutter's `SystemPermission`
 * model (see `validatePermission(...)` in scrips_core/utils/utils.dart).
 */
export interface SystemPermission {
  permissionKey: string;
  permissionResults?: PermissionResults;
}

/**
 * Session profile returned by GET /api/Account/GetUserDataToken plus
 * fields we derive from the JWT. Optional because some fields only come
 * from one source or the other.
 */
export interface UserData {
  // From JWT (always present once authenticated)
  userId: string;
  organizationId: string;

  /**
   * Setup-record GUID for the logged-in user. From GetUserDataToken; mirrors
   * Flutter `LoginUserData.id`. For a doctor this is the `doctorSetupId`, for
   * staff the `staffSetupId` — Account Settings opens the edit page with it
   * (`acc_settings_stack_widget.dart` passes `userData.id`). Distinct from
   * `userId` / `practitionerId` (the practitioner GUID). Optional — absent on
   * the JWT-only boot path before the profile fetch resolves.
   *
   * Field name is `id` because that is the literal key in the GetUserDataToken
   * response body (`getUserData` casts the raw JSON straight to `UserData`);
   * renaming to `setupRecordId` would require a mapping layer. This is the ONLY
   * `id` on `UserData` — no shadowing (the auth-user id is `userId`, the tenant
   * is `tenantRoleId` / `organizationId`).
   */
  id?: string;

  // From GetUserDataToken — optional because profile fetch can fail; the
  // api-client omits the PracticeId header rather than crossing tenants.
  // Most consumers need a practiceId-bearing user, so guard on this field
  // explicitly (see PROD-773).
  practiceId?: string;
  /**
   * Display name of the active practice. Mirrors Flutter
   * `LoginUserData.practiceName`, which the practice switcher writes
   * alongside `practiceId` on change (`calendar_screen.dart:2439-2440`)
   * and persists to secure storage. Used for the toolbar practice chip
   * label without having to wait on the practice-list fetch. Optional —
   * absent on older persisted sessions and on the JWT-only boot path.
   */
  practiceName?: string;

  // From JWT claims
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  /**
   * IANA timezone. Sourced from JWT `zoneinfo` claim at login; updated to the
   * Flutter sentinel `''` (empty string, `Timezone_mismatch_key`) when the
   * post-login timezone-mismatch sync fires. Used to skip re-sync on
   * subsequent boots when JWT and device tz agree.
   * Flutter ref: `login_data_source_impl.dart:218-229`.
   */
  timeZone?: string;

  // From GetUserDataToken
  practitionerId?: string;
  /**
   * Numeric tenant-role id (e.g. "19" = Phlebotomist, "4" = Nurse).
   * Distinct from `role` — `role` is the high-level
   * orgAdmin/staff/doctor bucket; `tenantRoleId` further specialises
   * inside the practice. Mirrors Flutter `LoginUserData.tenantRoleId`.
   * Used by the Lab linkage tab (Phlebotomist gate) — see
   * `isPhlebotomistRole` helper.
   */
  tenantRoleId?: string;
  speciality?: string;
  systemPermissions?: SystemPermission[];
  organizationLocations?: OrgLocation[];
  organizationCurrency?: string;
  isOnboarded?: boolean;
  isFirstTimeLogin?: boolean;
  isGuardian?: boolean;
  isPatient?: boolean;
  /**
   * The logged-in user's country (e.g. "United Arab Emirates"). Mirrors
   * Flutter `LoginUserData.country` (`login_user_data_model.dart:65`,
   * populated from `json["country"]` on the GetUserDataToken response).
   * `getUserData` casts the whole response to `UserData`, so this is
   * already present at runtime — typed here for the practice-country guard
   * (F-SET-123). May be absent for some users; consumers must guard.
   */
  country?: string | null;
  /**
   * Backend beta flag. Mirrors Flutter `LoginUserData.isBeta`
   * (`login_user_data_model.dart:83`). Gates access to beta-only surfaces
   * — currently the Coding & Claims **Reconciliation** sub-tab (PROD-823).
   * Returned by `/api/Account/GetUserDataToken` as a top-level boolean
   * (Flutter parses `json["isBeta"] ?? false` at `:128`).
   */
  isBeta?: boolean;
  hasExternalMRN?: boolean;
  brandingUrl?: string;
  medicationSystem?: string;
  resourceBasedIntegration?: boolean;
  maxDocumentUploadLimit?: number;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: string;
}

/**
 * Typed error thrown by auth-api endpoints when the server responds non-OK.
 * Callers branch on `.status` to mirror Flutter `login_repository_impl.dart:
 * 58-101` error mapping (401 → archived; 400 → CheckLockOutTime; 422 → device
 * limit; 404 → generic).
 *
 * Extends Error so existing `instanceof Error` callers stay compatible.
 */
export class ApiAuthError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string, message?: string) {
    super(message ?? `Auth endpoint returned ${status}`);
    this.name = 'ApiAuthError';
    this.status = status;
    this.body = body;
  }
}

/**
 * 409 from `GET /api/Account/GetUserDataToken` (or `/CheckUserDataToken`) —
 * the backend's session-handling filter detected an existing
 * `(UserId, AppType)` row in `User_Tokens` and is asking the client to
 * confirm a takeover (see PROD-931 product clarification).
 *
 * Mirrors Flutter `login_repository_impl.dart:78-93` + `:188-198` —
 * Flutter parses the body `{error?, userId?}` via `LoginErrorModel.fromJson`,
 * and on a non-null `userId` routes back into the `Right` path with a
 * partial `LoginUserData(id: userId, userId: userId)`. React surfaces the
 * same signal as a typed error so `auth-store` can open the
 * `<ActiveSessionConflictDialog />` instead of bouncing to logout.
 *
 * `userId` is the user the backend says is already logged in — passing it
 * back to `POST /api/Account/UpdateDeviceDetails` clears the conflicting
 * `User_Tokens` row so the second login can proceed (Flutter takeover
 * mechanic — `login_screen.dart:147` `bloc!.add(LogoutEvent(state.response))`
 * → after `LogoutState`, the screen re-fires `DoLoginEvent` at `:179-182`).
 */
export class ActiveSessionConflictError extends ApiAuthError {
  readonly conflictUserId: string;
  constructor(body: string, conflictUserId: string) {
    super(409, body, 'Active session conflict — another session is active');
    this.name = 'ActiveSessionConflictError';
    this.conflictUserId = conflictUserId;
  }
}

/**
 * Parse the 409 body shape `{error?: string, userId?: string}` (Flutter
 * `login_error_model.dart:29-32`). Returns the `userId` if the body is
 * shaped correctly, otherwise null — caller falls back to plain
 * `ApiAuthError` when the body is malformed (matches Flutter
 * `if (loginErrorModelFromJson(...).userId != null) ... else Failure(...)`).
 */
function parseConflictUserId(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'userId' in parsed
    ) {
      const userId = (parsed as { userId: unknown }).userId;
      if (typeof userId === 'string' && userId.length > 0) {
        return userId;
      }
    }
    return null;
  } catch {
    return null;
  }
}

async function postToken(body: URLSearchParams): Promise<LoginTokens> {
  const res = await fetch(`${BASE_URL}${DEV_CONFIG.auth.tokenEndpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
  const data = (await res.json()) as TokenResponse;
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  };
}

export function login(username: string, password: string): Promise<LoginTokens> {
  return postToken(
    new URLSearchParams({
      client_id: DEV_CONFIG.auth.clientId,
      client_secret: DEV_CONFIG.auth.clientSecret,
      grant_type: 'password',
      username,
      password,
      scope: DEV_CONFIG.auth.scope,
    }),
  );
}

export function refreshTokens(refreshToken: string): Promise<LoginTokens> {
  return postToken(
    new URLSearchParams({
      client_id: DEV_CONFIG.auth.clientId,
      client_secret: DEV_CONFIG.auth.clientSecret,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
  );
}

/**
 * POST `/api/Account/UpdateDeviceDetails` — fire on logout BEFORE clearing
 * local storage. Audit trail so the server knows the user logged out from
 * this device. Mirrors Flutter `login_data_source_impl.dart:325-344`.
 *
 * Body shape verbatim Flutter (`userId / appType / deviceId`). `deviceId`
 * is the lowercase literal `"web"` for browser clients (Flutter `kIsWeb`
 * short-circuit at line 331).
 *
 * Best-effort: callers should wrap in try/catch and proceed with logout
 * regardless of failure — the user must always be able to log out.
 */
export async function updateDeviceDetails(
  accessToken: string,
  body: { userId: string; appType: string; deviceId: string },
): Promise<void> {
  const ipAddress = await getClientIpAddress();
  const res = await fetch(`${BASE_URL}/api/Account/UpdateDeviceDetails`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      AppType: body.appType,
      UserId: body.userId,
      IpAddress: ipAddress,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`UpdateDeviceDetails returned ${res.status}`);
  }
}

/**
 * POST `/api/Account/UpdateUserTimeZone` — fire post-login when the
 * device timezone disagrees with the JWT `zoneinfo` claim AND the user is
 * not a Scrips admin. Mirrors Flutter `common_data_source_impl.dart:485-509`
 * + `update_timezone.dart:32-36`.
 *
 * Body shape verbatim Flutter (`userId / timeZone / deviceToken /
 * deviceType / deviceId`). `deviceToken` is `null` for web (no FCM token);
 * `deviceType` is `"Web"` (capital W) per `deviceWeb` constant.
 *
 * Best-effort: caller is fire-and-forget — UI doesn't block on the round
 * trip, and a failure is logged but doesn't fail login.
 */
export async function updateUserTimeZone(
  accessToken: string,
  body: {
    userId: string;
    timeZone: string;
    deviceToken: string | null;
    deviceType: string;
    deviceId: string;
  },
): Promise<void> {
  const ipAddress = await getClientIpAddress();
  const res = await fetch(`${BASE_URL}/api/Account/UpdateUserTimeZone`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      AppType: 'Practice Management',
      UserId: body.userId,
      IpAddress: ipAddress,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`UpdateUserTimeZone returned ${res.status}`);
  }
}

/**
 * Fetches the rich session profile (org locations, system permissions,
 * onboarding flags, etc.) that the JWT alone doesn't carry.
 *
 * Mirrors Flutter `login_data_source_impl.dart:166-254`. Used by:
 *   - Login path (`auth-store.login()`) — after `/connect/token` 200
 *   - Post-refresh re-sync (`auth-store.doRefresh()`) — after refresh 200
 *
 * **`UdId` is `"web"`** for browser clients. Flutter sends the device
 * UUID; web has no equivalent so the lowercase literal is the Flutter
 * web sentinel (`login_data_source_impl.dart:169` `udid = "web"`).
 *
 * **`SkipConflict` header was removed 2026-05-25 (PROD-945).** Earlier
 * Phase 7 work sent `SkipConflict: true` on the refresh re-sync path
 * matching Flutter `skipHeader: true`. PROD-931 backend audit found the
 * `.NET` `GenerateUserTokenDataCommandHandler.cs` parsed the header into
 * a DTO but never read it — the bypass was never wired up. Scrips.Identity
 * PR #488 removed the dead plumbing on the .NET side; the header send
 * from React (and Flutter, separately) is a pure no-op. Dropped.
 *
 * **409 handling**: parses the body `{userId}` and throws
 * `ActiveSessionConflictError` so `auth-store` can route to the
 * `<ActiveSessionConflictDialog />` ("another session detected" flow).
 * Falls back to plain `ApiAuthError(409, ...)` when the body has no
 * `userId` (matches Flutter `:90-91` `Failure("Failed to authorize from
 * server.")`).
 *
 * **Other non-OK statuses** → plain `ApiAuthError(.status, .body)` so
 * callers branch on `.status` (401 → archived, 422 → device limit, etc.).
 */
export async function getUserData(accessToken: string): Promise<UserData> {
  const ipAddress = await getClientIpAddress();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    AppType: 'Practice Management',
    UdId: 'web',
    IpAddress: ipAddress,
  };
  const res = await fetch(`${BASE_URL}/api/Account/GetUserDataToken`, {
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 409) {
      const conflictUserId = parseConflictUserId(text);
      if (conflictUserId) {
        throw new ActiveSessionConflictError(text, conflictUserId);
      }
    }
    throw new ApiAuthError(res.status, text);
  }
  return (await res.json()) as UserData;
}

/**
 * Cold-start / page-refresh session re-sync. Mirrors Flutter
 * `login_data_source_impl.dart:391-440` `getUserDataByToken({token})`.
 * Called from the boot-time effect in `auth-store` when an access token
 * is already in `localStorage` (analog of Flutter's `MenuContainerScreen.
 * initState` firing `GetUserDataByTokenEvent` at `menu_container_screen.
 * dart:104` when the app shell loads with a stored token but no
 * in-memory user data).
 *
 * Hits a **different endpoint** than `getUserData` — `CheckUserDataToken`
 * instead of `GetUserDataToken`. Same response shape. Same 409 semantics
 * (Flutter handles both identically in `login_repository_impl.dart:78-93`
 * and `:188-198`).
 *
 * **Headers differ** from `getUserData` — Flutter omits `UdId` here
 * (`login_data_source_impl.dart:393-401`). Mirror verbatim.
 *
 * 409 → `ActiveSessionConflictError` like `getUserData`. Other non-OK →
 * `ApiAuthError`.
 */
export async function checkUserData(accessToken: string): Promise<UserData> {
  const ipAddress = await getClientIpAddress();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    AppType: 'Practice Management',
    IpAddress: ipAddress,
  };
  const res = await fetch(`${BASE_URL}/api/Account/CheckUserDataToken`, {
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    if (res.status === 409) {
      const conflictUserId = parseConflictUserId(text);
      if (conflictUserId) {
        throw new ActiveSessionConflictError(text, conflictUserId);
      }
    }
    throw new ApiAuthError(res.status, text);
  }
  return (await res.json()) as UserData;
}

/**
 * Lockout-time response from `/api/Account/CheckLockOutTime`.
 *
 * Flutter `locked_time_model.dart:12-30`:
 *   { time: DateTime?, minute: double? }
 *
 * The backend only returns this populated when the account is actually
 * locked — Flutter parses it from a **201 response** (`login_data_source_impl.
 * dart:379`). Any other status (incl. 200) is treated as "not locked"
 * (`LockedTime()` empty).
 */
export interface LockedTime {
  time: string | null;
  minute: number | null;
}

/**
 * GET `/api/Account/CheckLockOutTime?emailId=<email>` — disambiguate
 * "wrong password" vs "account locked" on a `/connect/token` 400.
 *
 * Reactive only — called by `auth-store.login()` from inside the catch
 * block when `/connect/token` 400s. Flutter `login_repository_impl.dart:
 * 69-77` is the canonical chain.
 *
 * **HTTP verb**: Flutter `login_data_source.dart:16` declares `Future<LockedTime> checkLocked`
 * (no verb implied); the impl at `login_data_source_impl.dart:373-376` uses
 * `.get(...)`. Confirmed verb: GET. Empty body; email in query string.
 *
 * Returns Flutter-verbatim shape — empty `{time: null, minute: null}` when
 * not locked (mirrors Flutter `LockedTime()` constructor). Any network /
 * server error returns the same empty shape (silent, matches Flutter's
 * `catch DioException → return LockedTime()` at line 384-387).
 */
export async function checkLockOutTime(email: string): Promise<LockedTime> {
  try {
    const res = await fetch(
      `${BASE_URL}/api/Account/CheckLockOutTime?emailId=${encodeURIComponent(email)}`,
      {
        headers: { accept: 'application/json' },
      },
    );
    // Flutter explicitly checks `statusCode == 201`; anything else (incl.
    // 200) returns the empty default. Mirror verbatim.
    if (res.status === 201) {
      const data = (await res.json()) as Partial<LockedTime>;
      return {
        time: typeof data.time === 'string' ? data.time : null,
        minute: typeof data.minute === 'number' ? data.minute : null,
      };
    }
    return { time: null, minute: null };
  } catch {
    return { time: null, minute: null };
  }
}

/**
 * `/api/Account/CheckUserExistence` body field — Flutter sends a literal
 * GUID at `login_data_source_impl.dart:501`. Copy verbatim, do not regenerate.
 */
const CHECK_USER_EXISTENCE_KEY = 'ab7a3a6f-21cd-47f1-9597-7615b7c7f9f6';

/**
 * Response shape from `/api/Account/CheckUserExistence`. Flutter
 * `tenant_name_model.dart` parses `{tenant: string?}` — non-null tenant
 * means the email is known to the platform.
 */
export interface TenantName {
  tenant: string | null;
}

/**
 * POST `/api/Account/ForgotPassword` — trigger reset-code email (Phase 2).
 *
 * Flutter `forgot_password_data_source_impl.dart:22-39`. Body shape:
 *   { "email": <email> }
 *
 * Headers: `content-type: application/json`, `AppType: Practice Management`.
 *
 * Backend triggers email delivery server-side; response body is irrelevant
 * (Flutter returns `Success('')` regardless of payload). Successful 200/204
 * → resolve; any non-OK status → throws `ApiAuthError` so the page can
 * toast verbatim.
 */
export async function forgotPassword(email: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/Account/ForgotPassword`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      AppType: 'Practice Management',
    },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
}

/**
 * POST `/api/Account/CheckUserExistence` — fast-fail for unknown emails.
 *
 * **Not wired into PM login on web** — Flutter `login_bloc.dart:37` guards
 * the call with `if (event.isWeb != true)`, so web goes straight to
 * `/connect/token`. The wrapper exists for Phase 2+ flows (forgot password,
 * signup-via-access-code) that DO need to check existence before sending
 * a reset code.
 *
 * Body shape verbatim Flutter (`login_data_source_impl.dart:499-503`):
 *   { emailId, key: "ab7a3a6f-21cd-47f1-9597-7615b7c7f9f6" }
 *
 * Returns the parsed `{tenant}` shape (null when email unknown) or throws
 * `ApiAuthError` on HTTP failure.
 */

/**
 * Response from `/api/Account/UserDataLoginWithCode/{email}` — Flutter
 * `signup_user_data_model.dart`. Used by Phase 4 (Create Password via
 * email link) to greet the user with their name + photo on the
 * password-setup form. All fields optional / nullable per Flutter parity.
 */
export interface SignUpUserData {
  firstName: string | null;
  lastName: string | null;
  emailAddress: string | null;
  name: string | null;
  photoUrl: string | null;
  roleId: number | null;
}

/**
 * GET `/api/Account/UserDataLoginWithCode/{email}` — Phase 4 user-profile
 * fetch for the Create Password page. Flutter `create_password_data_source_impl
 * .dart:43-56`. Returns the user's profile so the page can show "Welcome
 * <firstName>" before asking for a password.
 *
 * On `!res.ok` throws `ApiAuthError` so the page can toast verbatim.
 */
export async function userDataLoginWithCode(
  email: string,
): Promise<SignUpUserData> {
  const res = await fetch(
    `${BASE_URL}/api/Account/UserDataLoginWithCode/${encodeURIComponent(email)}`,
    {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
  const data = (await res.json()) as Partial<SignUpUserData>;
  return {
    firstName: typeof data.firstName === 'string' ? data.firstName : null,
    lastName: typeof data.lastName === 'string' ? data.lastName : null,
    emailAddress:
      typeof data.emailAddress === 'string' ? data.emailAddress : null,
    name: typeof data.name === 'string' ? data.name : null,
    photoUrl: typeof data.photoUrl === 'string' ? data.photoUrl : null,
    roleId: typeof data.roleId === 'number' ? data.roleId : null,
  };
}

/**
 * Response from `/api/Account/LoginByCode` — Flutter
 * `signup_by_code_response_model.dart:13-36`. Both fields are nullable
 * server-side; treat null as failure (no token issued).
 */
export interface LoginByCodeResponse {
  passwordResetToken: string | null;
  emailAddress: string | null;
}

/**
 * POST `/api/Account/LoginByCode` — Phase 3a (reset-password access-code
 * entry) and Phase 5 (signup with access code).
 *
 * Body verbatim Flutter `sign_up_data_source_impl.dart:24-37`:
 *   { email, code, isresetpassword: bool }
 *
 * **`isresetpassword` is intentionally lowercase** — Flutter ships it that
 * way verbatim. Reset-password flow sends `true`; signup flow sends `false`.
 *
 * Response carries `passwordResetToken` which Phase 3b passes to
 * `setPassword` as the proof-of-code-verification.
 */
export async function loginByCode(params: {
  email: string;
  code: string;
  isResetPassword: boolean;
}): Promise<LoginByCodeResponse> {
  const res = await fetch(`${BASE_URL}/api/Account/LoginByCode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email,
      code: params.code,
      isresetpassword: params.isResetPassword,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
  const data = (await res.json()) as Partial<LoginByCodeResponse>;
  return {
    passwordResetToken:
      typeof data.passwordResetToken === 'string'
        ? data.passwordResetToken
        : null,
    emailAddress:
      typeof data.emailAddress === 'string' ? data.emailAddress : null,
  };
}

/**
 * POST `/api/Account/SetPassword` — Phases 3b (reset password) + 4 (create
 * password via email link).
 *
 * Body verbatim Flutter `create_password_data_source_impl.dart:24-40`:
 *   { email, password, confirmPassword, passwordResetToken }
 *
 * **Flutter always sets `confirmPassword = password` on the wire** (line
 * 30); the user-facing confirm-password field is for client-side validation
 * only. React mirrors: wrapper takes `password` once and writes both fields.
 *
 * On success → resolves void (Flutter returns `Success('')` regardless of
 * payload). On `!res.ok` throws `ApiAuthError`.
 */
export async function setPassword(params: {
  email: string;
  password: string;
  passwordResetToken: string;
}): Promise<void> {
  const res = await fetch(`${BASE_URL}/api/Account/SetPassword`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: params.email,
      password: params.password,
      confirmPassword: params.password,
      passwordResetToken: params.passwordResetToken,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
}

export async function checkUserExistence(email: string): Promise<TenantName> {
  const res = await fetch(`${BASE_URL}/api/Account/CheckUserExistence`, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      emailId: email,
      key: CHECK_USER_EXISTENCE_KEY,
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiAuthError(res.status, text);
  }
  const data = (await res.json()) as Partial<TenantName>;
  return { tenant: typeof data.tenant === 'string' ? data.tenant : null };
}
